import { getUserSettingsFromState } from 'components/Settings/UserSettings/userSettingsType';
import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getUserSetting } from 'selectors/userSettings';
import { useAuth } from './useAuth';
import licenseType from 'models/licenseType';
import { getSearchView } from '../selectors/search';
import { ActivitySearch } from '../components/Search/Results/helpers';

/**
 * Every category this user could search across. The licence decides whether 3rd
 * Party Data exists at all; nothing else varies. This is the whole population, so
 * it is what a figure has to be measured against to say whether it covers
 * everything — a saved default that leaves categories out is itself a narrowing.
 *
 * @returns {Record<string, boolean>}
 */
export function useAllCategories() {
  const { hasLicense } = useAuth();

  const hasIndustryLicense = hasLicense([licenseType.industry], true);
  return useMemo(
    () =>
      hasIndustryLicense
        ? {
            funding: true,
            tradeshows: true,
            publications: true,
            clinicalTrials: true,
            profile: true,
          }
        : {
            funding: true,
            tradeshows: true,
            publications: true,
            clinicalTrials: true,
          },
    [hasIndustryLicense]
  );
}

/**
 * The category selection a search starts with: every category, unless the user has
 * saved their own default. Both main search and the organisation profile seed from
 * this, so an untouched selection is identical on either side.
 *
 * @returns {Record<string, boolean>}
 */
export function useDefaultCategories() {
  const defaultSearchCategories = useAllCategories();

  const defaultSearchCategoriesFromSettings = useSelector(
    getUserSetting(getUserSettingsFromState.DefaultSearchCategories)
  );

  const searchCategories = useMemo(() => {
    if (defaultSearchCategoriesFromSettings) {
      return defaultSearchCategoriesFromSettings.reduce((acc, current) => {
        acc[current] = true;
        return acc;
      }, {});
    }

    return defaultSearchCategories;
  }, [defaultSearchCategories, defaultSearchCategoriesFromSettings]);

  return searchCategories;
}

function useDefaultSearchCategories(filters, setFilters, isSearchLoading) {
  const { hasLicense } = useAuth();
  const allowedReset = useRef(true);

  const hasIndustryLicense = hasLicense([licenseType.industry], true);
  const searchCategories = useDefaultCategories();

  // The active filters can change based on the view we're currently on.
  // "Activities" do not have the "3rd party data" option
  const view = useSelector(getSearchView);
  const isActivitiesView = ActivitySearch(view);

  const activeCategoryFilters = useMemo(() => {
    const activities = [];
    for (const key in filters) {
      if (filters[key] !== true) continue;
      if (isActivitiesView && key === 'profile') {
        continue;
      }
      activities.push(key);
    }
    return activities;
  }, [filters, isActivitiesView]);

  const activeCategoryFiltersCount = activeCategoryFilters.length;

  const setFiltersLocal = useRef(setFilters);
  useEffect(() => {
    // Force this effect to only run once so that filters aren't reset
    // when changing tabs since the filter array does change between tabs
    // but we only want to reset when the page first loads.
    if (!allowedReset.current) return;
    allowedReset.current = false;

    // If we've just entered the page and there's no active filters selected
    // then reset the filters
    if (activeCategoryFiltersCount === 0) {
      setFiltersLocal.current({ ...searchCategories });
    }

    if (!hasIndustryLicense && filters?.profile) {
      const { profile, ...noProfileFilters } = filters;
      setFiltersLocal.current(noProfileFilters);
    }
  }, [
    filters,
    hasIndustryLicense,
    searchCategories,
    activeCategoryFiltersCount,
  ]);

  const resetSearchCategoriesToDefault = useCallback(() => {
    setFiltersLocal.current({ ...searchCategories });
  }, [searchCategories]);

  return { resetSearchCategoriesToDefault };
}

export default useDefaultSearchCategories;
